import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

export const DevOpsTransformationCaseStudy = () => {
  return (
    <div className="min-h-screen bg-gray-950 dark:bg-gray-900">
      <Helmet>
        <title>DevOps Transformation Success Story | KOBRALABS</title>
        <meta name="description" content="Discover how KOBRALABS transformed Financial Services Ltd's development process, achieving 75% faster deployments and 90% reduction in incidents." />
        <meta name="keywords" content="DevOps, CI/CD, automation, infrastructure as code, deployment, monitoring" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-900/20 to-teal-900/20"></div>
        <div className="relative max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link to="/" className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-8">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Home
            </Link>
            
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                DevOps Transformation
              </h1>
              <p className="text-xl text-gray-300 mb-4">
                Financial Services Ltd achieved 75% faster deployments and 90% reduction in incidents
              </p>
              <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                <span className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full">DevOps</span>
                <span className="bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full">CI/CD</span>
                <span className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full">Automation</span>
                <span className="bg-orange-500/20 text-orange-300 px-3 py-1 rounded-full">Financial Services</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Executive Summary */}
      <section className="py-16 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-white mb-8">Executive Summary</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-800 p-6 rounded-xl">
                <h3 className="text-xl font-semibold text-white mb-4">The Challenge</h3>
                <p className="text-gray-300">
                  Financial Services Ltd, a major banking institution, was struggling with slow deployment 
                  cycles taking 2-3 weeks, frequent production incidents, and poor collaboration between 
                  development and operations teams, affecting their ability to deliver new features quickly.
                </p>
              </div>
              <div className="bg-gray-800 p-6 rounded-xl">
                <h3 className="text-xl font-semibold text-white mb-4">The Solution</h3>
                <p className="text-gray-300">
                  KOBRALABS implemented a comprehensive DevOps transformation including automated CI/CD 
                  pipelines, infrastructure as code, monitoring, and cultural changes to foster collaboration 
                  and continuous improvement.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Detailed Analysis */}
      <section className="py-16 px-4 bg-gray-950">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-white mb-12 text-center">Detailed Analysis</h2>
            
            {/* Technical Challenges */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">Technical Challenges</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-red-400 mb-3">Deployment Issues</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• 2-3 week deployment cycles</li>
                    <li>• Manual deployment processes</li>
                    <li>• Frequent rollbacks (30% of deployments)</li>
                    <li>• No automated testing</li>
                    <li>• Environment inconsistencies</li>
                  </ul>
                </div>
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-orange-400 mb-3">Operational Problems</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• 15+ production incidents per month</li>
                    <li>• 4-6 hour mean time to resolution</li>
                    <li>• Limited monitoring and alerting</li>
                    <li>• No centralized logging</li>
                    <li>• Poor incident response processes</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* DevOps Solution Architecture */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">DevOps Solution Architecture</h3>
              <div className="bg-gray-800 p-8 rounded-xl">
                <div className="grid md:grid-cols-3 gap-8">
                  <div>
                    <h4 className="text-lg font-semibold text-blue-400 mb-4">CI/CD Pipeline</h4>
                    <ul className="text-gray-300 space-y-2">
                      <li>• GitLab CI/CD automation</li>
                      <li>• Multi-stage deployment pipeline</li>
                      <li>• Automated testing (unit, integration, E2E)</li>
                      <li>• Security scanning integration</li>
                      <li>• Blue-green deployment strategy</li>
                      <li>• Automated rollback capabilities</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-green-400 mb-4">Infrastructure as Code</h4>
                    <ul className="text-gray-300 space-y-2">
                      <li>• Terraform for infrastructure management</li>
                      <li>• Ansible for configuration management</li>
                      <li>• Docker containerization</li>
                      <li>• Kubernetes orchestration</li>
                      <li>• Environment parity across stages</li>
                      <li>• Version-controlled infrastructure</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-purple-400 mb-4">Monitoring & Observability</h4>
                    <ul className="text-gray-300 space-y-2">
                      <li>• Prometheus for metrics collection</li>
                      <li>• Grafana for visualization</li>
                      <li>• ELK stack for logging</li>
                      <li>• Distributed tracing with Jaeger</li>
                      <li>• Real-time alerting</li>
                      <li>• Performance dashboards</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Implementation Process */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">Implementation Process</h3>
              <div className="space-y-6">
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-blue-400 mb-3">Phase 1: Assessment & Planning (4 weeks)</h4>
                  <p className="text-gray-300 mb-3">
                    Comprehensive analysis of current development and operations processes, identifying bottlenecks and improvement opportunities.
                  </p>
                  <ul className="text-gray-300 space-y-1">
                    <li>• Current state analysis and mapping</li>
                    <li>• Tool evaluation and selection</li>
                    <li>• Team structure assessment</li>
                    <li>• Security and compliance requirements</li>
                  </ul>
                </div>
                
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-green-400 mb-3">Phase 2: Infrastructure Setup (8 weeks)</h4>
                  <p className="text-gray-300 mb-3">
                    Building the foundational infrastructure using infrastructure as code principles and containerization.
                  </p>
                  <ul className="text-gray-300 space-y-1">
                    <li>• Kubernetes cluster setup</li>
                    <li>• CI/CD pipeline infrastructure</li>
                    <li>• Monitoring and logging stack</li>
                    <li>• Security and access controls</li>
                  </ul>
                </div>
                
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-purple-400 mb-3">Phase 3: Pipeline Development (10 weeks)</h4>
                  <p className="text-gray-300 mb-3">
                    Development and implementation of automated CI/CD pipelines with comprehensive testing and deployment strategies.
                  </p>
                  <ul className="text-gray-300 space-y-1">
                    <li>• Automated build and test pipelines</li>
                    <li>• Security scanning integration</li>
                    <li>• Deployment automation</li>
                    <li>• Rollback mechanisms</li>
                  </ul>
                </div>
                
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-orange-400 mb-3">Phase 4: Cultural Transformation (6 weeks)</h4>
                  <p className="text-gray-300 mb-3">
                    Training, process changes, and cultural transformation to embrace DevOps principles and practices.
                  </p>
                  <ul className="text-gray-300 space-y-1">
                    <li>• Team training and workshops</li>
                    <li>• Process documentation</li>
                    <li>• Change management</li>
                    <li>• Continuous improvement setup</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Technical Stack */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">Technical Stack</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-blue-400 mb-4">Development & CI/CD</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• GitLab for source control and CI/CD</li>
                    <li>• Docker for containerization</li>
                    <li>• Kubernetes for orchestration</li>
                    <li>• Helm for package management</li>
                    <li>• SonarQube for code quality</li>
                    <li>• OWASP ZAP for security scanning</li>
                  </ul>
                </div>
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-green-400 mb-4">Infrastructure & Monitoring</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• Terraform for infrastructure as code</li>
                    <li>• Ansible for configuration management</li>
                    <li>• Prometheus for metrics</li>
                    <li>• Grafana for visualization</li>
                    <li>• ELK stack for logging</li>
                    <li>• PagerDuty for alerting</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Results & Metrics */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">Results & Metrics</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 p-6 rounded-xl border border-green-500/30">
                  <div className="text-3xl font-bold text-green-400 mb-2">75%</div>
                  <div className="text-gray-300">Faster Deployments</div>
                  <div className="text-sm text-gray-400 mt-2">Reduced from 2 weeks to 3.5 days</div>
                </div>
                <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 p-6 rounded-xl border border-blue-500/30">
                  <div className="text-3xl font-bold text-blue-400 mb-2">90%</div>
                  <div className="text-gray-300">Fewer Incidents</div>
                  <div className="text-sm text-gray-400 mt-2">Reduced from 15 to 1.5 per month</div>
                </div>
                <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 p-6 rounded-xl border border-purple-500/30">
                  <div className="text-3xl font-bold text-purple-400 mb-2">60%</div>
                  <div className="text-gray-300">Faster Resolution</div>
                  <div className="text-sm text-gray-400 mt-2">MTTR reduced from 4 hours to 1.6 hours</div>
                </div>
                <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/20 p-6 rounded-xl border border-orange-500/30">
                  <div className="text-3xl font-bold text-orange-400 mb-2">100%</div>
                  <div className="text-gray-300">Automated Testing</div>
                  <div className="text-sm text-gray-400 mt-2">All deployments include automated tests</div>
                </div>
              </div>
            </div>

            {/* DevOps Metrics */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">DevOps Metrics</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-green-400 mb-4">Deployment Frequency</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• Daily deployments (vs. monthly before)</li>
                    <li>• 95% deployment success rate</li>
                    <li>• Zero-downtime deployments</li>
                    <li>• Automated rollback in &lt; 5 minutes</li>
                    <li>• Feature flag integration</li>
                  </ul>
                </div>
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-blue-400 mb-4">Quality & Reliability</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• 99.9% system availability</li>
                    <li>• 85% test coverage</li>
                    <li>• &lt; 1% change failure rate</li>
                    <li>• Automated security scanning</li>
                    <li>• Compliance automation</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Cultural Changes */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">Cultural Changes</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-purple-400 mb-4">Team Collaboration</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• Cross-functional DevOps teams</li>
                    <li>• Shared responsibility for deployments</li>
                    <li>• Regular blameless post-mortems</li>
                    <li>• Knowledge sharing sessions</li>
                    <li>• Collaborative problem-solving</li>
                  </ul>
                </div>
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-orange-400 mb-4">Process Improvements</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• Agile development practices</li>
                    <li>• Continuous feedback loops</li>
                    <li>• Data-driven decision making</li>
                    <li>• Automation-first mindset</li>
                    <li>• Continuous learning culture</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Security & Compliance */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">Security & Compliance</h3>
              <div className="bg-gray-800 p-6 rounded-xl">
                <div className="grid md:grid-cols-3 gap-8">
                  <div>
                    <h4 className="text-lg font-semibold text-red-400 mb-3">Security Integration</h4>
                    <ul className="text-gray-300 space-y-2">
                      <li>• Automated security scanning</li>
                      <li>• Vulnerability management</li>
                      <li>• Secrets management</li>
                      <li>• Access control automation</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-blue-400 mb-3">Compliance Automation</h4>
                    <ul className="text-gray-300 space-y-2">
                      <li>• SOX compliance automation</li>
                      <li>• Audit trail generation</li>
                      <li>• Policy enforcement</li>
                      <li>• Regulatory reporting</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-green-400 mb-3">Risk Management</h4>
                    <ul className="text-gray-300 space-y-2">
                      <li>• Automated risk assessment</li>
                      <li>• Incident response automation</li>
                      <li>• Business continuity planning</li>
                      <li>• Disaster recovery testing</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Future Roadmap */}
            <div>
              <h3 className="text-2xl font-semibold text-white mb-6">Future Roadmap</h3>
              <div className="bg-gray-800 p-6 rounded-xl">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-lg font-semibold text-purple-400 mb-3">Advanced Automation</h4>
                    <ul className="text-gray-300 space-y-2">
                      <li>• AI-powered incident response</li>
                      <li>• Predictive analytics for capacity planning</li>
                      <li>• Self-healing infrastructure</li>
                      <li>• Advanced monitoring with ML</li>
                      <li>• Automated performance optimization</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-green-400 mb-3">Platform Evolution</h4>
                    <ul className="text-gray-300 space-y-2">
                      <li>• Multi-cloud strategy</li>
                      <li>• Edge computing integration</li>
                      <li>• Serverless architecture adoption</li>
                      <li>• Advanced container orchestration</li>
                      <li>• Platform as a Service (PaaS)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-white mb-6">
              Ready to Transform Your Development Process?
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Let KOBRALABS help you implement DevOps practices that accelerate delivery, 
              improve quality, and foster a culture of continuous improvement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
              >
                Get Started Today
              </Link>
              <Link
                to="/case-studies"
                className="bg-gray-700 hover:bg-gray-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
              >
                View All Case Studies
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}; 